/**
 * @typedef {Object} SharedBrowserState
 * @property {number} focusedIndex - Index of the currently focused image.
 * @property {'single'|'group'} viewMode - Current view mode, e.g., 'single' or 'group'.
 * @property {Array} unifiedApidList - List of parent group APIDs in case of split groups.
 * @property {Array} thumbnails - Array of thumbnails, either parent group or child group APIDs.
 * @property {boolean} imageLoaded - Loading state of the image.
 * @property {string|null} imageError - Error message if the image fails to load.
 * @property {Set<string>} viewedImages - Arks of images the patron has focused. The consumer owns this state.
 */

export const initialState = {
  focusedIndex: -1,
  viewMode: 'single',

  /**
   * In the case of a split group (the group of images has been subdivided into 'items'),
   *   this is always the parent group of apids, even when viewing an item (child group)
   *   It is used to determine the new value of the focusedIndex when switching between view modes
   *   (which changes the array of images that focusedIndex refers to)
   *
   * In the case of an unsplit group, this has no value
   */
  unifiedApidList: [],

  thumbnails: [], // This is either the parent group of apids during viewMode = 'group', or a single child group of apids during viewMode = 'single'
  imageLoaded: false, // Loading state of ComboViewer
  imageError: null,
  viewedImages: new Set(), // Arks the patron has focused; rendered as a "viewed" badge by the image viewer
}

function reduceBatch(state, actions) {
  const next = actions.reduce((acc, curr) => reducer(acc, curr), state)
  // A view mode switch moves the same image to a new index; its error must survive since it won't refetch
  const sameFocusedImage = next.thumbnails[next.focusedIndex]?.ark === state.thumbnails[state.focusedIndex]?.ark
  const setsError = actions.some(({ type }) => type === 'SET_IMAGE_ERROR')
  return sameFocusedImage && !setsError ? { ...next, imageError: state.imageError } : next
}

export default function reducer(state, action) {
  if (!state) throw new Error('state is required')
  if (!action) throw new Error('action is required')
  switch (action.type) {
    case 'BATCH':
      return reduceBatch(state, action.value)
    case 'SET_FOCUSED_INDEX': {
      const ark = state.thumbnails[action.value]?.ark
      // Reuse the existing Set when the ark is already viewed so consumers memoized on
      // viewedImages (e.g. R2Image's thumbnail map) don't recompute on revisits.
      const viewedImages =
        ark && !state.viewedImages.has(ark) ? new Set(state.viewedImages).add(ark) : state.viewedImages
      // An image error belongs to the image that reported it, so drop it once another image takes focus
      const imageError = action.value === state.focusedIndex ? state.imageError : null
      return { ...state, focusedIndex: action.value, viewedImages, imageError }
    }
    case 'MARK_IMAGE_VIEWED': {
      // Marks an image viewed by ark without moving the focused index. The reference/split-screen
      // viewer uses this so its thumbnails get the "viewed" badge too (the main viewer marks via
      // SET_FOCUSED_INDEX, but the reference viewer's index lives in a different reducer).
      const viewedImages =
        action.value && !state.viewedImages.has(action.value)
          ? new Set(state.viewedImages).add(action.value)
          : state.viewedImages
      return { ...state, viewedImages }
    }
    case 'SET_VIEW_MODE':
      return { ...state, viewMode: action.value }
    case 'SET_UNIFIED_APID_LIST':
      return { ...state, unifiedApidList: action.value }
    case 'SET_THUMBNAILS': {
      // New thumbnails can swap the focused image without changing focusedIndex (e.g. switching subgroups)
      const focusedArkChanged = state.thumbnails[state.focusedIndex]?.ark !== action.value[state.focusedIndex]?.ark
      return { ...state, thumbnails: action.value, imageError: focusedArkChanged ? null : state.imageError }
    }
    case 'SET_IMAGE_LOADED':
      return { ...state, imageLoaded: action.value }
    case 'SET_IMAGE_ERROR':
      return { ...state, imageError: action.value }
    default:
      throw new Error(`Unhandled action type to ImageBrowsersReducer: ${action.type} in action: `, action)
  }
}
